import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, X, Save, Loader2, CheckCircle2, AlertCircle,
  AlertTriangle, ChevronDown,
  UserPlus, User, ShieldCheck, GraduationCap, Briefcase,
  KeyRound, Lock, Pencil, Puzzle,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { userService, organizationService, authService } from '../../services';

/**
 * Edit User Page
 * Form to edit an existing user in the system
 */

// Shared chrome — kept in lockstep with AddUserPage so the two forms
// read as a single design system.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]';
const CARD_FORM =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] overflow-hidden';

const PREVIEW_AVATAR_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededPreviewGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return PREVIEW_AVATAR_GRADIENTS[hash % PREVIEW_AVATAR_GRADIENTS.length];
};

// Participants aren't created/edited from this form — they sign up via a
// session code, so the tile is intentionally absent. A user record that still
// holds role:'participant' falls back to a neutral preview chip via
// LEGACY_ROLE_PILL below, but cannot be re-selected here.
const ROLE_TILE = {
  trainer: {
    Icon: GraduationCap,
    title: 'Trainer',
    desc: 'Runs sessions, manages workbooks.',
    chip: 'bg-cyan-50 text-cyan-600 ring-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:ring-cyan-500/20',
    pillCls: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',
  },
  client: {
    Icon: Briefcase,
    title: 'Client',
    desc: 'Manages an organization and its trainers.',
    chip: 'bg-violet-50 text-violet-600 ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20',
    pillCls: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30',
  },
  'super-admin': {
    Icon: ShieldCheck,
    title: 'Super Admin',
    desc: 'Full platform access. Use sparingly.',
    chip: 'bg-rose-50 text-rose-600 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20',
    pillCls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',
  },
};

const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  organizationId: 'Organization',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Inline date helpers — no library.
const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};
const formatRelative = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMs / 3600000);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffDay <= 14) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return formatDate(d);
};

const EditUserPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const formRef = useRef(null);
  const [originalRecord, setOriginalRecord] = useState(null);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [dangerDialog, setDangerDialog] = useState({ isOpen: false, title: '', message: '', confirmText: '', variant: 'danger', onConfirm: () => {} });
  const closeDangerDialog = () => setDangerDialog((d) => ({ ...d, isOpen: false }));

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'trainer',
    organizationId: '',
    status: 'active',
    department: '',
    jobTitle: '',
    bio: '',
    expertise: [],
    expertiseInput: '',
    // Per-trainer feature grants. Super-admin only; see User.features.
    simulationsEnabled: false,
    microSimsEnabled: false,
  });

  const [errors, setErrors] = useState({});

  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const initialFormDataRef = useRef(null);

  const checkIfDirty = () => {
    if (!initialFormDataRef.current) return false;
    return JSON.stringify(formData) !== initialFormDataRef.current;
  };

  const handleBackNavigation = (path) => {
    if (checkIfDirty()) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  const confirmLeave = () => {
    setShowBackConfirm(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userResponse, orgsResponse] = await Promise.all([
        userService.getUser(userId),
        organizationService.getOrganizations({ limit: 100 }),
      ]);

      const user = userResponse.data || userResponse;
      setOriginalRecord(user);
      const loadedData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        // Preserve a legacy 'participant' value if the record still holds it —
        // the tile grid will render no selection so the admin must explicitly
        // pick a new role to save.
        role: user.role || 'trainer',
        organizationId: user.organization?._id || user.organizationId || '',
        // Solo-trainer plan controls (only used when organizationType==='individual').
        organizationType: user.organization?.type || user.primaryOrganization?.type || 'standard',
        plan: user.subscriptionRecord?.plan || user.organization?.plan || user.primaryOrganization?.plan || 'free',
        status: user.status || 'active',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
        bio: user.bio || '',
        expertise: user.expertise || [],
        expertiseInput: '',
        simulationsEnabled: Boolean(user.features?.simulations),
        microSimsEnabled: Boolean(user.features?.microSims),
      };
      setFormData(loadedData);
      initialFormDataRef.current = JSON.stringify(loadedData);
      setOrganizations(orgsResponse.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAddExpertise = () => {
    if (formData.expertiseInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        expertise: [...prev.expertise, prev.expertiseInput.trim()],
        expertiseInput: '',
      }));
    }
  };
  const handleRemoveExpertise = (index) => {
    setFormData((prev) => ({
      ...prev,
      expertise: prev.expertise.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    // Solo (individual) trainers have their own auto-created org, so they don't
    // pick one from the list — skip the org requirement for them.
    const needsOrg = formData.role === 'client'
      || (formData.role === 'trainer' && formData.organizationType !== 'individual');
    if (needsOrg && !formData.organizationId) {
      newErrors.organizationId = 'Organization is required for this role';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setIsSubmitting(true);
    try {
      const { organizationId, expertiseInput, organizationType, simulationsEnabled, microSimsEnabled, ...userData } = formData;
      const submitData = {
        ...userData,
        organization: organizationId || null,
      };
      // Feature grants only apply to trainers, and the API accepts them from a
      // super-admin only — sending the nested shape the controller expects.
      if (formData.role === 'trainer') {
        submitData.features = {
          simulations: Boolean(simulationsEnabled),
          microSims: Boolean(microSimsEnabled),
        };
      }
      // `plan` is only meaningful for solo (individual) trainers; drop it otherwise
      // so it can't accidentally touch an org-scoped user.
      if (!(formData.role === 'trainer' && organizationType === 'individual')) {
        delete submitData.plan;
      }
      if (formData.role !== 'trainer') {
        delete submitData.bio;
        delete submitData.expertise;
      }
      await userService.updateUser(userId, submitData);
      toast.success('User updated successfully!');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.message || 'Failed to update user');
      console.error('Error updating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    handleBackNavigation('/admin/users');
  };

  // ⌘+Enter / Ctrl+Enter submit shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting && checkIfDirty()) {
        e.preventDefault();
        formRef.current?.requestSubmit?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, formData]);

  // ────── Edit-only handlers ──────
  const handleSendPasswordReset = async () => {
    if (!formData.email) {
      toast.error('No email on file for this user');
      return;
    }
    setIsResettingPassword(true);
    try {
      await authService.forgotPassword(formData.email);
      toast.success('Password reset link sent.');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset link');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const isInactive = formData.status === 'inactive';
  const fullName = `${formData.firstName} ${formData.lastName}`.trim();

  const handleToggleActive = () => {
    if (isInactive) {
      setDangerDialog({
        isOpen: true,
        title: 'Reactivate user',
        message: `Restore access for ${fullName || 'this user'}? They will be able to log in immediately.`,
        confirmText: 'Reactivate',
        variant: 'success',
        onConfirm: () => {
          // Flip the status field — the user must Save to commit. Keeps
          // a single source of truth for status mutation (the form).
          setFormData((prev) => ({ ...prev, status: 'active' }));
          toast('Status set to Active. Click Save changes to apply.', { icon: '📝' });
        },
      });
    } else {
      setDangerDialog({
        isOpen: true,
        title: 'Deactivate user',
        message: `Temporarily block ${fullName || 'this user'} from logging in. You can reactivate them later.`,
        confirmText: 'Deactivate',
        variant: 'danger',
        onConfirm: () => {
          setFormData((prev) => ({ ...prev, status: 'inactive' }));
          toast('Status set to Inactive. Click Save changes to apply.', { icon: '📝' });
        },
      });
    }
  };

  const handleDelete = () => {
    setDangerDialog({
      isOpen: true,
      title: 'Delete user',
      message: `Permanently remove ${fullName || 'this account'} and unassign them from all sessions. This cannot be undone.`,
      confirmText: 'Delete user',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await userService.deleteUser(userId);
          toast.success(`${fullName || 'User'} has been deleted`);
          // Bypass dirty-check — the record no longer exists.
          initialFormDataRef.current = JSON.stringify(formData);
          navigate('/admin/users');
        } catch (error) {
          toast.error(error.message || 'Failed to delete user');
        }
      },
    });
  };

  // ────── Derived render values ──────
  const isDirty = checkIfDirty();
  const initials = `${(formData.firstName || '').charAt(0)}${(formData.lastName || '').charAt(0)}`.toUpperCase();
  const previewGradient = seededPreviewGradient(initials || 'AA');
  // Legacy 'participant' records still need a preview chip even though the
  // tile is no longer selectable. Anything unknown falls back to trainer.
  const LEGACY_PARTICIPANT_PILL = {
    title: 'Participant',
    pillCls: 'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  };
  const role = ROLE_TILE[formData.role]
    || (formData.role === 'participant' ? LEGACY_PARTICIPANT_PILL : ROLE_TILE.trainer);
  const previewOrgName = organizations.find((o) => o._id === formData.organizationId)?.name || '—';
  const errorKeys = Object.keys(errors).filter((k) => !!errors[k]);
  const errorLabelList = errorKeys.map((k) => FIELD_LABELS[k] || k).join(', ');
  // Solo (individual) trainers keep their own auto-created org — don't show the
  // org picker for them (matches the Add User form).
  const showOrgSelect = formData.role === 'client'
    || (formData.role === 'trainer' && formData.organizationType !== 'individual');

  // Activity rows — only render the card if at least one is present.
  const activityRows = [
    originalRecord?.lastLoginAt && { label: 'Last login', date: originalRecord.lastLoginAt },
    originalRecord?.lastSessionAt && { label: 'Last session', date: originalRecord.lastSessionAt },
    originalRecord?.invitationAcceptedAt && { label: 'Invitation accepted', date: originalRecord.invitationAcceptedAt },
  ].filter(Boolean).slice(0, 3);

  return (
    <DashboardLayout role="super-admin">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-6 w-full px-4 lg:px-6 py-6">
        {/* ────── LEFT RAIL ────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
          {/* A. Page identity */}
          <div className={`${CARD_BASE} p-5`}>
            <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-amber-50 ring-1 ring-inset ring-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20">
              <UserPlus size={18} strokeWidth={1.75} />
            </span>
            <div className="flex items-center mt-3 flex-wrap gap-1">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Edit user</h2>
              {isDirty && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Update profile, role, and access for this user.</p>

            {loading ? (
              <div className="mt-4 space-y-1.5">
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
              </div>
            ) : (
              <dl className="text-xs text-neutral-500 dark:text-neutral-400 mt-4 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <dt>Created</dt>
                  <dd className="tabular-nums text-neutral-700 dark:text-neutral-200">{formatDate(originalRecord?.createdAt)}</dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt>Last updated</dt>
                  <dd className="tabular-nums text-neutral-700 dark:text-neutral-200">{formatRelative(originalRecord?.updatedAt)}</dd>
                </div>
                {originalRecord?.lastLoginAt && (
                  <div className="flex items-center justify-between gap-2">
                    <dt>Last login</dt>
                    <dd className="tabular-nums text-neutral-700 dark:text-neutral-200">{formatRelative(originalRecord.lastLoginAt)}</dd>
                  </div>
                )}
              </dl>
            )}

            <button
              type="button"
              onClick={() => handleBackNavigation('/admin/users')}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center gap-1 mt-4 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.75} />
              Back to Users
            </button>
          </div>

          {/* B. Live preview */}
          <div className={`${CARD_BASE} overflow-hidden`}>
            <div className="bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900/60 px-4 py-2 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 border-b border-neutral-100 dark:border-neutral-800/70">
              Preview
            </div>
            <div className="p-4 space-y-3">
              {loading ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-3/4" />
                      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-1/2" />
                    </div>
                  </div>
                  <div className="h-4 rounded-full bg-neutral-200 dark:bg-neutral-800 animate-pulse w-20" />
                  <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-2/3" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    {initials ? (
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${previewGradient} flex items-center justify-center text-white font-semibold text-sm shadow-sm`}>
                        {initials}
                      </div>
                    ) : (
                      <div className="w-11 h-11 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-xs font-semibold">
                        AA
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${fullName ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
                        {fullName || 'Full name'}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{formData.email || '—'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset capitalize ${role.pillCls}`}>
                    {role.title}
                  </span>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{previewOrgName}</p>
                </>
              )}
            </div>
          </div>

          {/* C. Recent activity (only if data present + not loading) */}
          {!loading && activityRows.length > 0 && (
            <div className={`${CARD_BASE} p-4`}>
              <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 mb-2">Recent activity</p>
              {activityRows.map((row) => (
                <div key={row.label} className="flex items-center gap-2 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                  <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.label}</span>
                  <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{formatRelative(row.date)}</span>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* ────── MAIN FORM ────── */}
        <main>
          <form ref={formRef} onSubmit={handleSubmit} className={CARD_FORM}>
            {/* Hero strip */}
            <div className="h-14 bg-gradient-to-r from-primary-50 via-white to-white dark:from-primary-500/5 dark:via-neutral-900 dark:to-neutral-900 border-b border-neutral-100 dark:border-neutral-800/70 px-6 flex items-center gap-3">
              <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20">
                <UserPlus size={16} strokeWidth={1.75} />
              </span>
              <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Edit user</h1>
            </div>

            <div className="p-6 lg:p-8 space-y-8">
              {loading ? (
                <SkeletonForm />
              ) : (
                <>
                  {/* Error summary */}
                  {errorKeys.length > 0 && (
                    <div className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 flex items-start gap-3 dark:bg-rose-500/10 dark:ring-rose-500/20">
                      <AlertCircle size={18} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-rose-900 dark:text-rose-200">Please fix the errors below</p>
                        <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{errorLabelList}</p>
                      </div>
                    </div>
                  )}

                  {/* ── Personal information ── */}
                  <section>
                    <SectionHeader Icon={User} title="Personal information" subtitle="How this person identifies on the platform." />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                      <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter first name" required fullWidth error={errors.firstName} />
                      <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter last name" required fullWidth error={errors.lastName} />

                      {/* Locked email with explicit override */}
                      <div className="space-y-1">
                        <Input
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="user@example.com"
                          required
                          fullWidth
                          error={errors.email}
                          disabled={!emailUnlocked}
                          readOnly={!emailUnlocked}
                          className={emailUnlocked ? '' : 'bg-neutral-50 dark:bg-neutral-900/40 text-neutral-500'}
                          rightIcon={!emailUnlocked ? <Lock size={14} className="text-neutral-400" /> : null}
                        />
                        {!emailUnlocked ? (
                          <button type="button" onClick={() => setEmailUnlocked(true)} className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center gap-1 transition-colors">
                            <Pencil size={12} strokeWidth={1.75} /> Change email
                          </button>
                        ) : (
                          <p className="text-xs text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
                            <AlertTriangle size={12} strokeWidth={1.75} /> Changing the email will require re-verification on next login.
                          </p>
                        )}
                      </div>

                      {/* Phone with "+" prefix */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Phone Number</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none">+</span>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="1 (555) 123-4567"
                            className={`w-full pl-7 pr-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ${errors.phone ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 transition`}
                          />
                        </div>
                        {errors.phone && <p className="text-xs text-rose-600 dark:text-rose-400">{errors.phone}</p>}
                      </div>
                    </div>
                  </section>

                  {/* ── Role & organization ── */}
                  <section>
                    <SectionHeader Icon={ShieldCheck} title="Role & organization" subtitle="What they can access and which org they belong to." />

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      {Object.entries(ROLE_TILE).map(([value, def]) => {
                        const RoleIcon = def.Icon;
                        const selected = formData.role === value;
                        return (
                          <label
                            key={value}
                            className={`block rounded-2xl bg-white dark:bg-neutral-900 p-4 cursor-pointer text-center transition-all ${
                              selected
                                ? 'ring-2 ring-primary-500/60 bg-primary-50/40 dark:bg-primary-500/5'
                                : 'ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 hover:ring-neutral-300 dark:hover:ring-neutral-700 hover:-translate-y-0.5'
                            }`}
                          >
                            <input
                              type="radio"
                              name="role"
                              value={value}
                              checked={selected}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            <span className={`mx-auto inline-flex w-9 h-9 rounded-xl items-center justify-center ring-1 ring-inset ${def.chip}`}>
                              <RoleIcon size={18} strokeWidth={1.75} />
                            </span>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-2">{def.title}</p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{def.desc}</p>
                          </label>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {showOrgSelect && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                            Organization <span className="text-rose-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              name="organizationId"
                              value={formData.organizationId}
                              onChange={handleChange}
                              required
                              className={`appearance-none w-full pl-3 pr-9 py-2.5 text-sm rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ${errors.organizationId ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 transition cursor-pointer`}
                            >
                              <option value="">Select an organization</option>
                              {organizations.map((org) => (
                                <option key={org._id} value={org._id}>{org.name}</option>
                              ))}
                            </select>
                            <ChevronDown size={14} strokeWidth={1.75} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" />
                          </div>
                          {errors.organizationId && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{errors.organizationId}</p>}
                        </div>
                      )}

                      {/* Solo-trainer plan — admin can switch Free/Pro for free */}
                      {formData.role === 'trainer' && formData.organizationType === 'individual' && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                            Plan <span className="text-[11px] font-normal text-neutral-400">(complimentary — no payment)</span>
                          </label>
                          <div className="relative">
                            <select
                              name="plan"
                              value={formData.plan}
                              onChange={handleChange}
                              className="appearance-none w-full pl-3 pr-9 py-2.5 text-sm rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 transition cursor-pointer"
                            >
                              <option value="free">Free</option>
                              <option value="pro">Pro (complimentary)</option>
                            </select>
                            <ChevronDown size={14} strokeWidth={1.75} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" />
                          </div>
                          <p className="text-[11px] text-neutral-400 mt-1">Downgrading to Free resets any participant-limit override.</p>
                        </div>
                      )}

                      {formData.role !== 'trainer' && (
                        <>
                          <Input label="Job Title" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Enter job title" fullWidth />
                          <Input label="Department" name="department" value={formData.department} onChange={handleChange} placeholder="Enter department" fullWidth />
                        </>
                      )}
                    </div>
                  </section>

                  {/* ── Feature access (super-admin only) ──
                      Off by default: simulations is granted to named trainers rather
                      than shipped to everyone. Hiding the sidebar entry is cosmetic —
                      the API enforces the same flag, so revoking here really does
                      revoke access, not just the menu item. */}
                  {formData.role === 'trainer' && (
                    <section>
                      <SectionHeader
                        Icon={Puzzle}
                        title="Feature access"
                        subtitle="Features granted to this trainer individually."
                      />
                      <label className="flex items-start gap-3 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 px-4 py-3.5 cursor-pointer">
                        <input
                          type="checkbox"
                          name="simulationsEnabled"
                          checked={formData.simulationsEnabled}
                          onChange={(e) => setFormData((f) => ({ ...f, simulationsEnabled: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                            Simulations
                          </span>
                          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                            Lets this trainer author and run team simulations. When off, the
                            Simulations menu is hidden and the simulation APIs reject their requests.
                          </span>
                        </span>
                      </label>
                      <label className="mt-2.5 flex items-start gap-3 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 px-4 py-3.5 cursor-pointer">
                        <input
                          type="checkbox"
                          name="microSimsEnabled"
                          checked={formData.microSimsEnabled}
                          onChange={(e) => setFormData((f) => ({ ...f, microSimsEnabled: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                            Micro simulations
                          </span>
                          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                            Lets this trainer run paired micro simulations in their sessions.
                            Pilot access while the feature is being rolled out.
                          </span>
                        </span>
                      </label>
                    </section>
                  )}

                  {/* ── Trainer profile ── */}
                  {formData.role === 'trainer' && (
                    <section>
                      <SectionHeader Icon={GraduationCap} title="Trainer profile" subtitle="Bio and expertise areas." />
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Bio</label>
                          <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleChange}
                            placeholder="Brief description about the trainer…"
                            rows={3}
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition resize-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Areas of Expertise</label>
                          <div className="flex gap-2">
                            <Input
                              name="expertiseInput"
                              value={formData.expertiseInput}
                              onChange={handleChange}
                              placeholder="e.g., Leadership, Sales"
                              fullWidth
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddExpertise();
                                }
                              }}
                            />
                            <Button type="button" variant="secondary" onClick={handleAddExpertise}>Add</Button>
                          </div>
                          {formData.expertise.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {formData.expertise.map((exp, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center gap-1 ring-1 ring-inset ring-neutral-200 bg-white px-2 py-0.5 rounded-full text-xs text-neutral-700 dark:bg-neutral-900 dark:ring-neutral-800 dark:text-neutral-200"
                                >
                                  {exp}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExpertise(index)}
                                    aria-label={`Remove ${exp}`}
                                    className="text-neutral-400 hover:text-rose-600 ml-1 transition-colors"
                                  >
                                    <X size={12} strokeWidth={2} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Status & access ── */}
                  <section>
                    <SectionHeader Icon={ShieldCheck} title="Status & access" subtitle="Toggle login access and send a password reset link." />

                    <div className="space-y-4">
                      {/* Status chips */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                          Status <span className="text-rose-500">*</span>
                        </label>
                        <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Status">
                          {STATUS_OPTIONS.map((opt) => {
                            const active = formData.status === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, status: opt.value }))}
                                aria-pressed={active}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition-colors ${
                                  active
                                    ? 'bg-primary-50 ring-primary-200 text-primary-700 dark:bg-primary-500/10 dark:ring-primary-500/30 dark:text-primary-300'
                                    : 'bg-white dark:bg-neutral-900 ring-neutral-200 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                                }`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Reset password row */}
                      <div className="rounded-xl bg-neutral-50/70 dark:bg-neutral-900/40 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 flex items-center gap-3">
                        <span className="inline-flex w-9 h-9 rounded-xl items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 text-neutral-600 dark:text-neutral-400 flex-shrink-0">
                          <KeyRound size={16} strokeWidth={1.75} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Reset password</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                            Send a password-reset email to {formData.email || 'this user'}.
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleSendPasswordReset} disabled={isResettingPassword || !formData.email}>
                          {isResettingPassword ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                          Send reset link
                        </Button>
                      </div>
                    </div>
                  </section>

                  {/* ── Danger zone ── */}
                  <section>
                    <div className="flex items-start gap-3 mb-4">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-rose-50 ring-1 ring-inset ring-rose-100 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20">
                        <AlertTriangle size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">Danger zone</h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">These actions can't be easily undone.</p>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-rose-200/70 dark:ring-rose-500/20 divide-y divide-rose-100/70 dark:divide-rose-500/10 overflow-hidden">
                      <div className="flex items-center justify-between gap-3 p-4">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                            {isInactive ? 'Reactivate user' : 'Deactivate user'}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 max-w-md">
                            {isInactive
                              ? 'Restore access for this user.'
                              : 'Temporarily block this user from logging in.'}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleToggleActive} className="ring-1 ring-inset ring-rose-200 text-rose-700 hover:bg-rose-50 dark:ring-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10">
                          {isInactive ? 'Reactivate' : 'Deactivate'}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between gap-3 p-4">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Delete user</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 max-w-md">
                            Permanently remove this account and unassign them from all sessions. Cannot be undone.
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleDelete} className="ring-1 ring-inset ring-rose-200 text-rose-700 hover:bg-rose-50 dark:ring-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10">
                          Delete
                        </Button>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>

            {/* Sticky footer (hidden while loading) */}
            {!loading && (
              <div className="sticky bottom-0 z-10 bg-white/85 dark:bg-neutral-900/85 backdrop-blur-md border-t border-neutral-100 dark:border-neutral-800/70 px-6 py-3 flex items-center justify-between gap-3">
                <p className="text-xs hidden sm:flex items-center gap-1.5">
                  {isDirty ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-amber-700 dark:text-amber-300">You have unsaved changes</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={12} className="text-emerald-600 dark:text-emerald-400" />
                      <span className="text-emerald-600 dark:text-emerald-400">All changes saved</span>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
                    <X size={14} className="mr-1.5" /> Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!isDirty || isSubmitting}
                    className={isDirty ? 'shadow-[0_4px_12px_-4px_rgba(255,138,0,0.45)] ring-2 ring-primary-500/30' : ''}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>
                    ) : (
                      <><Save size={14} className="mr-1.5" /> Save changes</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </main>
      </div>

      {/* Unsaved changes confirm */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={confirmLeave}
        title="Discard unsaved changes?"
        message="You've made changes that haven't been saved. If you leave now, those changes will be lost."
        confirmText="Discard changes"
        cancelText="Keep editing"
        variant="danger"
      />

      {/* Multiplexed danger-zone confirm */}
      <ConfirmDialog
        isOpen={dangerDialog.isOpen}
        onClose={closeDangerDialog}
        onConfirm={() => { dangerDialog.onConfirm?.(); }}
        title={dangerDialog.title}
        message={dangerDialog.message}
        confirmText={dangerDialog.confirmText}
        cancelText="Cancel"
        variant={dangerDialog.variant}
      />
    </DashboardLayout>
  );
};

const SectionHeader = ({ Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400">
      <Icon size={16} strokeWidth={1.75} />
    </span>
    <div>
      <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const SkeletonForm = () => (
  <div className="space-y-8">
    {[User, ShieldCheck, ShieldCheck].map((Icon, idx) => (
      <section key={idx}>
        <SectionHeader Icon={Icon} title=" " subtitle=" " />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-xl bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
          ))}
        </div>
      </section>
    ))}
  </div>
);

export default EditUserPage;
